import React, { useMemo, useState } from "react";
import { Check, Plus, Tag } from "lucide-react";
import { Input } from "../ui/input";

export const SUGGESTED_CATEGORIES = ["Breakfast", "Lunch", "Dinner", "Main Course", "Starters", "Snacks", "Bakery Items", "Desserts", "Coffee", "Tea", "Cold Drinks", "Beverages", "Combos", "Sides"];

const key = (rid) => `kt_menu_categories_${rid || "default"}`;
export const loadSavedCategories = (rid) => { try { return JSON.parse(localStorage.getItem(key(rid)) || "[]"); } catch { return []; } };
export const saveCategory = (rid, cat) => {
  const list = loadSavedCategories(rid);
  if (!list.some((c) => c.toLowerCase() === cat.toLowerCase())) localStorage.setItem(key(rid), JSON.stringify([...list, cat]));
};
export const normalizeCategory = (v) => v.trim().replace(/\s+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Chips of the restaurant's own categories + suggestions, plus a free-text field for new ones.
export function CategoryPicker({ value, onChange, existing = [], testId = "item-category" }) {
  const [custom, setCustom] = useState("");
  const options = useMemo(() => {
    const seen = new Set();
    const out = [];
    [...existing, ...SUGGESTED_CATEGORIES].forEach((c) => { const k = c.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push({ name: c, own: existing.some((e) => e.toLowerCase() === k) }); } });
    if (value && !seen.has(value.toLowerCase())) out.unshift({ name: value, own: true });
    return out;
  }, [existing, value]);
  const addCustom = () => { const c = normalizeCategory(custom); if (!c) return; onChange(c); setCustom(""); };
  return (
    <div className="space-y-3" data-testid={testId}>
      {existing.length > 0 && <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Your categories</p>}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o.name} type="button" onClick={() => onChange(o.name)} aria-pressed={value === o.name} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition-colors ${value === o.name ? "bg-brand-100 text-brand-700 ring-brand-200" : o.own ? "bg-white text-ink ring-surface-300 hover:bg-surface-100" : "bg-surface-100 text-ink-soft ring-transparent hover:bg-surface-200"}`} data-testid={`${testId}-opt-${o.name.toLowerCase().replace(/\s+/g, "-")}`}>
            {value === o.name ? <Check className="h-3 w-3" /> : o.own ? <Tag className="h-3 w-3" /> : null}{o.name}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} placeholder="Or type a new category, e.g. Momos" className="h-10" data-testid={`${testId}-custom`} />
        <button type="button" onClick={addCustom} disabled={!custom.trim()} className="inline-flex h-10 shrink-0 items-center gap-1 rounded-full bg-graphite px-4 text-sm font-medium text-white disabled:opacity-40" data-testid={`${testId}-custom-add`}><Plus className="h-4 w-4" /> Add</button>
      </div>
      {!value && <p className="text-xs text-red-600" data-testid={`${testId}-required`}>Pick or add a category so the item shows under the right section.</p>}
    </div>
  );
}
